import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ArrowLeft } from 'lucide-react'
import Button from '../../components/ui/Button'
import Alert from '../../components/ui/Alert'
import QRScannerModal from '../../components/QRScannerModal'
import { qrService } from '../../services/dumperApi'
import type { ActiveAssetOption } from '../../types'
import type { QrActiveAsset } from '../../types/dumper'

// Mapea QR.activeAssets[i] → ActiveAssetOption del picker. Mantener TODOS los
// campos: el picker los muestra (licensePlate, residente) y la pantalla de
// registro los lee de `location.state.pickedAsset` para hidratar el form
// (residentId, materialType) sin re-resolver el QR.
const qrAssetToOption = (qa: QrActiveAsset, licensePlate: string | null = null): ActiveAssetOption => ({
  assetId: qa.assetId,
  conditionIncomeName: qa.conditionIncomeName,
  incomeRate: qa.incomeRate,
  incomeUnit: qa.incomeUnit,
  transportUnit: qa.transportUnit ?? null,
  materialUnit: qa.materialUnit ?? null,
  dumperDealType: qa.dumperDealType,
  nextRemisionNumber: qa.nextRemisionNumber,
  materialAssets: qa.materialAssets ?? [],
  materialType: qa.materialType ?? null,
  buildingName: qa.buildingName ?? null,
  licensePlate: licensePlate ?? null,
  dealId: qa.asset?.dealId ?? null,
  operationId: qa.asset?.operationId ?? null,
  residentId: qa.residentId ?? null,
  residentName: qa.residentName ?? null,
  residentPhone: qa.residentPhone ?? null,
  loadingZone: qa.loadingZone ?? null,
  unloadingZone: qa.unloadingZone ?? null,
})

const RegisterDumperTripQR: React.FC = () => {
  const navigate = useNavigate()
  const [open, setOpen] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const handleDecode = async (text: string) => {
    setOpen(false)
    if (!text) {
      setError('QR vacío')
      return
    }
    try {
      const res = await qrService.resolve(text)
      if (res.registrationFlow !== 'DUMPER_HOURMETER') {
        setError('El QR no corresponde a una volqueta activa')
        return
      }
      // Solo assets de contexto volqueta — una machinery mixta podría tener
      // assets no-volqueta que no aplican al flujo de viajes. licensePlate
      // viene a nivel del response (es de la machinery, común a todos los assets).
      const options = (res.activeAssets ?? [])
        .filter((qa) => qa.isVolqueta)
        .map((qa) => qrAssetToOption(qa, res.licensePlate ?? null))
      // 0 → error (back-compat: backend viejo sin activeAssets[] cae al flujo
      // directo si trae activeAsset flat). 1 → directo. N → picker.
      if (options.length === 0) {
        if (res.activeAsset?.id) {
          navigate(`/dumper-hourmeters/register?qr=${encodeURIComponent(text)}`)
          return
        }
        setError('El QR no tiene una volqueta activa configurada')
        return
      }
      if (options.length === 1) {
        navigate(`/dumper-hourmeters/register?qr=${encodeURIComponent(text)}`)
        return
      }
      navigate('/dumper-hourmeters/select-asset', {
        state: { assets: options, qrCode: text },
      })
    } catch (e: any) {
      setError(e?.response?.data?.message || 'No se pudo resolver el QR')
    }
  }

  return (
    <div className="min-h-screen bg-gray-100">
      <header className="bg-retri-blue px-3 py-3 flex items-center gap-2">
        <Button variant="ghost" size="icon" onClick={() => navigate(-1)} className="text-white">
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <h1 className="text-white font-semibold">Escanear volqueta</h1>
      </header>

      <main className="max-w-md mx-auto p-4 space-y-4">
        {error && <Alert variant="error">{error}</Alert>}
        <Button onClick={() => setOpen(true)} className="w-full h-11">
          Abrir cámara
        </Button>
      </main>

      <QRScannerModal open={open} onClose={() => setOpen(false)} onDecode={handleDecode} />
    </div>
  )
}

export default RegisterDumperTripQR
